import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Suspense, lazy, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { ProtectedLayout } from './components/ProtectedLayout.tsx';
import { OfflineBanner } from './components/OfflineBanner.tsx';
import { MaintenanceBanner } from './components/MaintenanceBanner.tsx';
import { PortalRedirect } from './components/PortalRedirect.tsx';
import { SESSION_ROUTES } from './lib/session-routes.ts';

const LoginScreen = lazy(() => import('./screens/auth/LoginScreen.tsx'));
const RegisterScreen = lazy(() => import('./screens/auth/RegisterScreen.tsx'));
const MfaScreen = lazy(() => import('./screens/auth/MfaScreen.tsx'));
const MfaSetupScreen = lazy(() => import('./screens/auth/MfaSetupScreen.tsx'));
const PasswordResetScreen = lazy(() => import('./screens/auth/PasswordResetScreen.tsx'));
const InviteRedeemScreen = lazy(() => import('./screens/auth/InviteRedeemScreen.tsx'));
const VerifyEmailScreen = lazy(() => import('./screens/auth/VerifyEmailScreen.tsx'));
const DashboardScreen = lazy(() => import('./screens/dashboard/DashboardScreen.tsx'));
const CaseListScreen = lazy(() => import('./screens/case/CaseListScreen.tsx'));
const CaseDetailScreen = lazy(() => import('./screens/case/CaseDetailScreen.tsx'));
const CaseDetailsScreen = lazy(() => import('./screens/case/CaseDetailsScreen.tsx'));
const SessionScreen = lazy(() => import('./screens/session/SessionScreen.tsx'));
const CaseSessionsScreen = lazy(() => import('./screens/case/CaseSessionsScreen.tsx'));
const CaseBillingScreen = lazy(() => import('./screens/billing/CaseBillingScreen.tsx'));
const ComplianceScreen = lazy(() => import('./screens/compliance/ComplianceScreen.tsx'));
const DataSubjectRequestScreen = lazy(() => import('./screens/compliance/DataSubjectRequestScreen.tsx'));
const DocumentsScreen = lazy(() => import('./screens/documents/DocumentsScreen.tsx'));
const AdminScreen = lazy(() => import('./screens/admin/AdminScreen.tsx'));
const UsersScreen = lazy(() => import('./screens/admin/UsersScreen.tsx'));
const PlansScreen = lazy(() => import('./screens/admin/PlansScreen.tsx'));
const PracticesScreen = lazy(() => import('./screens/admin/PracticesScreen.tsx'));
const PlatformAuditScreen = lazy(() => import('./screens/admin/PlatformAuditScreen.tsx'));
const UsageAnalyticsScreen = lazy(() => import('./screens/admin/UsageAnalyticsScreen.tsx'));
const NotificationsScreen = lazy(() => import('./screens/notifications/NotificationsScreen.tsx'));
const CreatePracticeScreen = lazy(() => import('./screens/practice/CreatePracticeScreen.tsx'));
const GetStartedScreen = lazy(() => import('./screens/onboarding/GetStartedScreen.tsx'));
const PendingActionsScreen = lazy(() => import('./screens/onboarding/PendingActionsScreen.tsx'));
const DpaGateScreen = lazy(() => import('./screens/practice/DpaGateScreen.tsx'));
const TermsGateScreen = lazy(() => import('./screens/practice/TermsGateScreen.tsx'));
const RoleAcknowledgementScreen = lazy(() => import('./screens/RoleAcknowledgementScreen.tsx'));
const PracticeSettingsScreen = lazy(() => import('./screens/practice/PracticeSettingsScreen.tsx'));
const PracticeSetupScreen = lazy(() => import('./screens/practice/PracticeSetupScreen.tsx'));
const SetupRoundScreen = lazy(() => import('./screens/SetupRoundScreen.tsx'));
const PracticeCasesScreen = lazy(() => import('./screens/practice/PracticeCasesScreen.tsx'));
const ClientAddedScreen = lazy(() => import('./screens/case/ClientAddedScreen.tsx'));
const AgreementsScreen = lazy(() => import('./screens/practice/AgreementsScreen.tsx'));
const PracticeOverviewScreen = lazy(() => import('./screens/practice/PracticeOverviewScreen.tsx'));
const PracticeTeamScreen = lazy(() => import('./screens/practice/PracticeTeamScreen.tsx'));
function LegacyPracticeInvoicingRedirect(): JSX.Element {
  const { practiceId = '' } = useParams();
  return <Navigate to={`/practices/${practiceId}/invoicing`} replace />;
}
const PracticeBillingScreen = lazy(() => import('./screens/practice/PracticeBillingScreen.tsx'));
const PracticePlanScreen = lazy(() => import('./screens/practice/PracticePlanScreen.tsx'));
const PracticeTemplatesScreen = lazy(() => import('./screens/practice/PracticeTemplatesScreen.tsx'));
const EnquiryWizardScreen = lazy(() => import('./screens/enquiry/EnquiryWizardScreen.tsx'));
const QuickAddClientScreen = lazy(() => import('./screens/case/QuickAddClientScreen.tsx'));
const EnquiryListScreen = lazy(() => import('./screens/enquiry/EnquiryListScreen.tsx'));
const EnquiryDetailScreen = lazy(() => import('./screens/enquiry/EnquiryDetailScreen.tsx'));
const ContractSignScreen = lazy(() => import('./screens/enquiry/ContractSignScreen.tsx'));
const PrivacyNoticeScreen = lazy(() => import('./screens/compliance/PrivacyNoticeScreen.tsx'));
const HelpScreen = lazy(() => import('./screens/help/HelpScreen.tsx'));
const BehaviourTargetsScreen = lazy(
  () => import('./screens/aba/BehaviourTargetsScreen.tsx'),
);
const SkillTargetsScreen = lazy(
  () => import('./screens/aba/SkillTargetsScreen.tsx'),
);
const CaseHistoryScreen = lazy(
  () => import('./screens/case/CaseHistoryScreen.tsx'),
);
const CaseAgreementScreen = lazy(
  () => import('./screens/case/CaseAgreementScreen.tsx'),
);
const CaseMembersScreen = lazy(
  () => import('./screens/case/CaseMembersScreen.tsx'),
);
const CaseAccessRequestScreen = lazy(
  () => import('./screens/case/CaseAccessRequestScreen.tsx'),
);
const CaseAccessRegisterScreen = lazy(
  () => import('./screens/case/CaseAccessRegisterScreen.tsx'),
);
const TargetLibraryScreen = lazy(() => import('./screens/aba/TargetLibraryScreen.tsx'));
const TimesheetScreen = lazy(() => import('./screens/timesheet/TimesheetScreen.tsx'));
const ProfileScreen = lazy(() => import('./screens/profile/ProfileScreen.tsx'));
const SessionManagementScreen = lazy(
  () => import('./screens/profile/SessionManagementScreen.tsx'),
);
const ProfileCredentialsScreen = lazy(
  () => import('./screens/profile/ProfileCredentialsScreen.tsx'),
);
const ProfileCpdScreen = lazy(() => import('./screens/profile/ProfileCpdScreen.tsx'));
const ProfileAgreementsScreen = lazy(
  () => import('./screens/profile/ProfileAgreementsScreen.tsx'),
);
/** FBA/BSP moved onto the Behaviour Targets page itself (owner, 2026-08-30) — they are
 *  components of a behaviour target, not their own screens. Kept as a redirect, not a
 *  removed route: an already-sent notification's linkUrl still names the old path. */
function LegacyBehaviourTargetsRedirect(): JSX.Element {
  const { caseId = '' } = useParams();
  return <Navigate to={`/cases/${caseId}/behaviour-targets`} replace />;
}
const IncidentScreen = lazy(() => import('./screens/aba/IncidentScreen.tsx'));
const NotFoundScreen = lazy(() => import('./screens/NotFoundScreen.tsx'));

function LoadingFallback(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center gap-2 text-muted-foreground"
    >
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      <span className="text-sm">Loading…</span>
    </div>
  );
}

export default function App(): JSX.Element {
  return (
    <>
      <OfflineBanner />
      <MaintenanceBanner />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/register" element={<RegisterScreen />} />
          <Route path="/mfa" element={<MfaScreen />} />
          <Route path="/mfa/setup" element={<MfaSetupScreen />} />
          <Route path="/invite" element={<InviteRedeemScreen />} />
          {/* Email verification (v7.26): link-click handler + blocking interstitial.
              Outside ProtectedLayout so the gate redirect here does not loop. */}
          <Route path="/verify-email" element={<VerifyEmailScreen />} />
          <Route path="/password-reset" element={<PasswordResetScreen />} />
          {/* Public contract signing - unauthenticated, outside the app shell. */}
          <Route path="/contracts/sign/:token" element={<ContractSignScreen />} />
          {/* PUBLIC on purpose: Art. 13 information must reach people with no
              account - a prospective user reading it before they register, a
              contract signatory who never signs in, a former user. */}
          <Route path="/privacy" element={<PrivacyNoticeScreen />} />
          {/* Public share-recipient download - unauthenticated, outside the app shell. */}
          {/* Authenticated shell: redirect guard + subscription gate (banner / interstitial). */}
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<DashboardScreen />} />
            {/* ToS 4.2 — the shell blocks here while an undertaking is due. Inside the
                layout so the gate's redirect is suppressed once we are on it. */}
            <Route path="/role-acknowledgement" element={<RoleAcknowledgementScreen />} />
            {/* THE PORTAL IS RETIRED (owner, 2026-08-22). One view, one URL, whoever is
                looking: the case surfaces already resolve what to show from the actor's own
                capabilities, so a second address for the same view was the structural half
                of the UI problem. These redirects keep every bookmark and every notification
                ever sent — rows that outlive the code — landing on the surviving view.
                `replace` so the dead address never enters history. */}
            <Route path="/portal" element={<Navigate to="/cases" replace />} />
            <Route path="/portal/cases/:caseId" element={<PortalRedirect />} />
            <Route path="/portal/cases/:caseId/programme" element={<PortalRedirect to="skill-targets" />} />
            <Route path="/portal/cases/:caseId/programme/skill-targets" element={<PortalRedirect to="skill-targets" />} />
            <Route path="/portal/cases/:caseId/programme/behaviour-targets" element={<PortalRedirect to="behaviour-targets" />} />
            <Route path="/portal/cases/:caseId/programme/functional-assessments" element={<PortalRedirect to="fba" />} />
            <Route path="/portal/cases/:caseId/programme/bsp" element={<PortalRedirect to="bip" />} />
            <Route path="/portal/cases/:caseId/documents" element={<PortalRedirect to="documents" />} />
            <Route path="/portal/cases/:caseId/invoices" element={<PortalRedirect to="invoicing" />} />
            <Route path="/portal/cases/:caseId/team" element={<PortalRedirect to="members" />} />
            <Route path="/portal/cases/:caseId/agreement" element={<PortalRedirect to="agreement" />} />
            <Route path="/portal/cases/:caseId/access" element={<PortalRedirect to="access-register" />} />
            <Route
              path="/portal/cases/:caseId/session/:encounterId"
              element={<PortalRedirect to="session" />}
            />
            <Route path="/notifications" element={<NotificationsScreen />} />
            <Route path="/get-started" element={<GetStartedScreen />} />
            {/* FB-0021: the one address for what is addressed to you. The dashboard panel
                is unreachable for a client-only account, and get-started greets an account
                with no relationships, so neither could be where a tutor accepts. */}
            <Route path="/pending" element={<PendingActionsScreen />} />
            <Route path="/practices/new" element={<CreatePracticeScreen />} />
            <Route path="/practices/:practiceId" element={<PracticeOverviewScreen />} />
            <Route path="/practices/:practiceId/team" element={<PracticeTeamScreen />} />
            <Route path="/practices/:practiceId/invoicing" element={<PracticeBillingScreen />} />
            <Route path="/practices/:practiceId/billing" element={<LegacyPracticeInvoicingRedirect />} />
            <Route path="/practices/:practiceId/plan" element={<PracticePlanScreen />} />
            <Route path="/practices/:practiceId/templates" element={<PracticeTemplatesScreen />} />
            <Route path="/practices/:practiceId/dpa" element={<DpaGateScreen />} />
            <Route path="/practices/:practiceId/terms" element={<TermsGateScreen />} />
            <Route path="/practices/:practiceId/agreements" element={<AgreementsScreen />} />
            <Route path="/practices/:practiceId/settings" element={<PracticeSettingsScreen />} />
            <Route path="/practices/:practiceId/setup" element={<PracticeSetupScreen />} />
            <Route path="/setup" element={<SetupRoundScreen />} />
            {/* PRACTICE-SCOPED business surfaces (slice D). Business pages belong behind an
                explicit practice: an unscoped route acts on whichever practice is "active",
                which is invisible in the chrome, so a per-practice figure opened an
                all-practice page. The migration is done — no unscoped enquiries/compliance/
                target-library/cases-quick-add route remains; the caseload's own /cases stays
                unscoped by design (primary-nav "My caseload"), not as leftover migration debt. */}
            <Route path="/practices/:practiceId/enquiries" element={<EnquiryListScreen />} />
            <Route path="/practices/:practiceId/enquiries/new" element={<EnquiryWizardScreen />} />
            <Route path="/practices/:practiceId/enquiries/:enquiryId" element={<EnquiryDetailScreen />} />
            <Route path="/practices/:practiceId/compliance" element={<ComplianceScreen />} />
            {/* One request, as a case file. Nested under the practice because a data-subject
                request belongs to ONE controller, like the queue it came from. */}
            <Route
              path="/practices/:practiceId/compliance/requests/:requestId"
              element={<DataSubjectRequestScreen />}
            />
            <Route path="/practices/:practiceId/target-library" element={<TargetLibraryScreen />} />
            <Route path="/practices/:practiceId/cases" element={<PracticeCasesScreen />} />
            <Route path="/practices/:practiceId/cases/quick-add" element={<QuickAddClientScreen />} />
            <Route path="/practices/:practiceId/cases/added/:caseId" element={<ClientAddedScreen />} />
            {/* Authenticated staff preview of the signing page (v7.197) — replaces
                opening the client's real signing link, which wrongly marked the
                contract 'viewed' and exposed working sign/decline actions. */}
            <Route path="/contracts/:id/preview" element={<ContractSignScreen />} />
            <Route path="/cases" element={<CaseListScreen />} />
            <Route path="/cases/:caseId" element={<CaseDetailScreen />} />
            <Route path="/cases/:caseId/details" element={<CaseDetailsScreen />} />
            <Route path="/cases/:caseId/agreement" element={<CaseAgreementScreen />} />
            <Route path="/cases/:caseId/members" element={<CaseMembersScreen />} />
            {/* §8.2-§8.4 clinical step-up: ask for access, and the register of who holds it. */}
            <Route path="/cases/:caseId/request-access" element={<CaseAccessRequestScreen />} />
            <Route path="/cases/:caseId/access-register" element={<CaseAccessRegisterScreen />} />
            <Route path={SESSION_ROUTES.staff} element={<SessionScreen />} />
            <Route path="/cases/:caseId/sessions" element={<CaseSessionsScreen />} />
            <Route path="/cases/:caseId/invoicing" element={<CaseBillingScreen />} />
            {/* Links stored before 2026-08-28 (notifications, bookmarks) still say billing. */}
            <Route path="/cases/:caseId/billing" element={<PortalRedirect to="invoicing" />} />
            <Route path="/cases/:caseId/documents" element={<DocumentsScreen />} />
            <Route
              path="/cases/:caseId/skill-targets"
              element={<SkillTargetsScreen />}
            />
            <Route
              path="/cases/:caseId/history"
              element={<CaseHistoryScreen />}
            />
            <Route
              path="/cases/:caseId/behaviour-targets"
              element={<BehaviourTargetsScreen />}
            />
            <Route path="/cases/:caseId/fba" element={<LegacyBehaviourTargetsRedirect />} />
            <Route path="/cases/:caseId/bip" element={<LegacyBehaviourTargetsRedirect />} />
            <Route path="/cases/:caseId/incidents" element={<IncidentScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/profile/sessions" element={<SessionManagementScreen />} />
            <Route path="/profile/credentials" element={<ProfileCredentialsScreen />} />
            <Route path="/profile/cpd" element={<ProfileCpdScreen />} />
            <Route path="/profile/agreements" element={<ProfileAgreementsScreen />} />
            <Route path="/timesheet" element={<TimesheetScreen />} />
            <Route path="/admin" element={<AdminScreen />} />
            <Route path="/admin/users" element={<UsersScreen />} />
            <Route path="/admin/practices" element={<PracticesScreen />} />
            <Route path="/admin/plans" element={<PlansScreen />} />
            <Route path="/admin/audit" element={<PlatformAuditScreen />} />
            <Route path="/admin/usage" element={<UsageAnalyticsScreen />} />
            <Route path="/help" element={<HelpScreen />} />
            <Route path="*" element={<NotFoundScreen />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}
